import React, { useState, useContext } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { db } from '../../firebase';
import { UserContext } from '../../contexts/UserContext';
import {
  runTransaction,
  increment,
  doc,
  setDoc,
  collection,
  updateDoc,
  getDocs,
  query,
  where,
  getDoc,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

const stripePromise = loadStripe('pk_live_51ND6ZkLg4uiaMuu2u948KSNhWfsnRv7F2QIIkLv8KNsLNzRaQHZR5c0GsB6jxKjCxM0157w8BuZIMq1EDJnIyCUq00Va8NbzTC');

const CheckoutForm = ({ pickupTime }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [isUpdating, setIsUpdating] = useState(false);
  const [cardError, setCardError] = useState('');
  const [showSuccesModal, setShowSuccesModal] = useState(false);

  const storedPanier = localStorage.getItem('panier');
  const parsedPanier = storedPanier ? JSON.parse(storedPanier) : [];

  const [clientInfo, setClientInfo] = useState({
    nom: '',
    prenom: '',
    email: '',
    tele: '',
    adresse: '',
  });

  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [promoDocId, setPromoDocId] = useState(null);
  const [promoDetails, setPromoDetails] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handlePromoCodeChange = (e) => {
    setPromoCode(e.target.value);
  };

  const calculateSubtotal = () => {
    return parsedPanier.reduce(
      (total, plat) => total + parseFloat(plat.prix) * plat.quantity,
      0
    );
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const total = subtotal - discount;
    return Math.max(total, 0).toFixed(2);
  };

  const getEmailContent = async (id) => {
    try {
      const emailDoc = await getDoc(doc(db, 'email', id));
      if (emailDoc.exists()) {
        return emailDoc.data();
      }
      console.error('Document email non trouvé');
      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'email :", error);
      return null;
    }
  };

  const handleApplyPromoCode = async () => {
    try {
      if (!promoCode.trim()) {
        alert('Veuillez saisir un code promo.');
        return;
      }

      const q = query(collection(db, 'promos'), where('promo_code', '==', promoCode.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        alert('Code promo non trouvé.');
        return;
      }

      const promoDoc = querySnapshot.docs[0];
      const promoData = promoDoc.data();
      const today = new Date();

      const validFrom =
        promoData.valid_from instanceof Date
          ? promoData.valid_from
          : promoData.valid_from.toDate();

      const validTo =
        promoData.valid_to instanceof Date
          ? promoData.valid_to
          : promoData.valid_to.toDate();

      if (!(promoData.statut && validFrom <= today && validTo >= today)) {
        alert('Code promo invalide ou expiré.');
        return;
      }

      if (promoData.usage_count >= promoData.usage_limit) {
        alert("Limite d'utilisation du code promo atteinte.");
        return;
      }

      let discountAmount = 0;
      const subtotal = calculateSubtotal();

      if (promoData.promo_type === 'percentage') {
        discountAmount = (subtotal * promoData.promo_valeur) / 100;
      } else if (promoData.promo_type === 'flat') {
        discountAmount = promoData.promo_valeur;
      }

      discountAmount = Math.min(discountAmount, subtotal);

      setDiscount(discountAmount);
      setPromoDocId(promoDoc.id);
      setPromoDetails({
  id: promoDoc.id,
  code: promoData.promo_code,
  type: promoData.promo_type,
  valeur: promoData.promo_valeur,
  remise: discountAmount,
});

      alert('Code promo appliqué avec succès !');
    } catch (error) {
      console.error("Erreur lors de l'application du code promo :", error);
      alert("Erreur lors de l'application du code promo.");
    }
  };
  const getClientPaymentMessage = (status) => {
  switch (status) {
    case 'requires_action':
      return "Votre banque demande une confirmation de paiement. Merci de suivre les étapes affichées pour finaliser votre commande.";

    case 'requires_payment_method':
      return "Le paiement a été refusé. Merci de vérifier les informations de votre carte ou d'utiliser un autre moyen de paiement.";

    case 'processing':
      return "Votre paiement est en cours de traitement. Merci de patienter quelques instants.";

    case 'canceled':
      return "Le paiement a été annulé. Votre commande n'a pas été enregistrée.";

    case 'requires_capture':
      return "Votre paiement a été autorisé avec succès.";

    default:
      return "Le paiement n'a pas pu être finalisé. Merci de réessayer dans quelques instants ou de contacter votre banque.";
  }
};

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsUpdating(true);
    setCardError('');

    try {
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error(
  "Les informations de paiement sont incomplètes. Merci de vérifier votre carte bancaire."
);
      }

      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${clientInfo.prenom} ${clientInfo.nom}`,
          email: clientInfo.email,
          phone: clientInfo.tele,
          address: {
            line1: clientInfo.adresse,
          },
        },
      });

      if (paymentMethodError) {
        setCardError(paymentMethodError.message || 'Erreur de carte.');
        setIsUpdating(false);
        return;
      }

      const amountInCents = Math.round(Number(calculateTotal()) * 100);

      if (!amountInCents || amountInCents <= 0) {
        throw new Error(
  "Le montant de votre commande est invalide. Merci de rafraîchir la page et de réessayer."
);
      }

      const response = await fetch('https://api-7zyk3qftka-uc.a.run.app/api/payment_comm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          amount: amountInCents,
        }),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
       throw new Error(
  "Une erreur est survenue lors du traitement de votre paiement. Merci de réessayer dans quelques instants."
);
      }

const data = await response.json();

if (!data.paymentIntentId || !data.status || !data.clientSecret) {
  throw new Error(
  "Une erreur technique est survenue lors du traitement du paiement. Merci de réessayer dans quelques instants."
);
}

let paymentIntent = {
  id: data.paymentIntentId,
  status: data.status,
};

if (data.status === 'requires_action') {
  const result = await stripe.confirmCardPayment(data.clientSecret);

  if (result.error) {
    setCardError(
   "Le paiement n'a pas été confirmé. Merci de vérifier votre application bancaire ou votre SMS de validation puis de réessayer."
  );
    setIsUpdating(false);
    return;
  }

  paymentIntent = result.paymentIntent;
}

if (paymentIntent.status !== 'requires_capture') {
  throw new Error(getClientPaymentMessage(paymentIntent.status));
}

// const isRequiresCapture = paymentIntent.status === 'requires_capture';


// const isSucceeded = paymentIntent.status === 'succeeded';
// const isRequiresCapture = paymentIntent.status === 'requires_capture';

const date_commande = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}).format(new Date());

const date_service = `${new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}).format(new Date())} ${pickupTime}`;

const newOrderId = await runTransaction(db, async (transaction) => {
  const counterRef = doc(db, 'metadata', 'commandeCounter');
  const counterSnapshot = await transaction.get(counterRef);

  if (!counterSnapshot.exists()) {
    throw new Error("Le compteur de commandes n'existe pas.");
  }

  const currentCounter = counterSnapshot.data().currentValue;
  const newCounter = currentCounter + 1;

  transaction.update(counterRef, { currentValue: newCounter });

  return newCounter;
});

await setDoc(doc(db, 'commande', newOrderId.toString()), {
  id: newOrderId.toString(),
  nom: clientInfo.nom,
  prenom: clientInfo.prenom,
  email: clientInfo.email,
  tele: clientInfo.tele,
  adresse: clientInfo.adresse,
  plats: parsedPanier.map(item => ({
    id: item.id,
    nom: item.nom,
    prix: item.prix,
    taille: item.taille,
    addons: item.addons,
    quantite: item.quantity,
    instructions: item.instructions,
    total: (parseFloat(item.prix) * item.quantity).toFixed(2),
  })),
  date_commande,
  date_service,
  montant: calculateTotal(),

  code_promo_utilise: promoDetails ? promoDetails.code : '',
  promo_id: promoDetails ? promoDetails.id : '',
  promo_type: promoDetails ? promoDetails.type : '',
  promo_valeur: promoDetails ? promoDetails.valeur : 0,
  promo_remise: promoDetails ? promoDetails.remise.toFixed(2) : '0.00',

  statut_commande: 'En cours',
  statut_paiement: paymentIntent.status,
  paymentIntentId: paymentIntent.id,
});

// code promo seulement après création commande si tu le gardes
if (promoDocId) {
  await updateDoc(doc(db, 'promos', promoDocId), {
    usage_count: increment(1),
  });
}
localStorage.removeItem('panier');
setShowSuccesModal(true);
// Si le paiement est déjà finalisé
// if (isSucceeded) {
//   const emailAnn = await getEmailContent('receptionCommande');

//   if (emailAnn) {
//     await fetch('https://api-7zyk3qftka-uc.a.run.app/api/email-annulation', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({
//         email: clientInfo.email,
//         nom: clientInfo.nom,
//         prenom: clientInfo.prenom,
//         objet: emailAnn.objet,
//         emailContent: emailAnn.texte,
//       }),
//     });
//   }

//   await fetch('https://api-7zyk3qftka-uc.a.run.app/api/sendZapier', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({
//       orderId: newOrderId,
//       client: clientInfo.nom,
//       montant: calculateTotal(),
//       date_commande,
//       date_service,
//       plats: parsedPanier,
//     }),
//   });

//   localStorage.removeItem('panier');
//   setShowSuccesModal(true);
// } else if (isRequiresCapture) {
//   // Commande bien créée, mais paiement seulement autorisé
//   localStorage.removeItem('panier');
//   setShowSuccesModal(true);
// } else {
//   setCardError(`Commande créée, statut Stripe actuel : ${paymentIntent.status}`);
// }

setIsUpdating(false);
    } catch (error) {
      console.error('Erreur paiement / commande :', error);
     setCardError(
  error?.message ||
  "Une erreur est survenue lors du traitement de votre commande. Merci de réessayer."
);
      setIsUpdating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row mb-4">
        <div className="col-6">
          <div className="form-group">
            <input
              type="text"
              id="nom"
              name="nom"
              className="form-control form-stl"
              placeholder="Nom"
              value={clientInfo.nom}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-6">
          <div className="form-group">
            <input
              type="text"
              id="prenom"
              placeholder="Prénom"
              name="prenom"
              className="form-control form-stl"
              value={clientInfo.prenom}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-6">
          <div className="form-group">
            <input
              type="email"
              id="email"
              placeholder="Email"
              name="email"
              className="form-control form-stl"
              value={clientInfo.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-6">
          <div className="form-group">
            <input
              type="tel"
              id="tele"
              placeholder="Téléphone"
              name="tele"
              className="form-control form-stl"
              value={clientInfo.tele}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-12">
          <div className="form-group">
            <textarea
              id="adresse"
              placeholder="Adresse"
              name="adresse"
              className="form-control form-stl"
              value={clientInfo.adresse}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-12 mt-3 mb-3">
          <div className="form-group">
            <label htmlFor="card" className="small-tl mb-3">Carte de Crédit</label>
            <CardElement id="card" />
            {cardError && (
              <p style={{ color: 'red', marginTop: '10px' }}>
                {cardError}
              </p>
            )}
          </div>
        </div>

        <div className="col-12 mt-3 mb-3">
          <div className="form-group">
            <label htmlFor="promoCode">Code Promo</label>
            <div className="input-group">
              <input
                type="text"
                id="promoCode"
                className="form-control"
                value={promoCode}
                onChange={handlePromoCodeChange}
              />
              <div className="input-group-append">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApplyPromoCode}
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 mb-3">
          <button
            type="submit"
            className="btn hover-mr btn-org w-100 mt-3"
            disabled={isUpdating || !stripe}
          >
            {isUpdating ? (
              <span>
                Payer {calculateTotal()}€ <span className="spinner-border spinner-border-sm"></span>
              </span>
            ) : (
              <span>Payer {calculateTotal()}€</span>
            )}
          </button>
        </div>
      </div>

      <Modal
        show={showSuccesModal}
        onHide={() => navigate('/boutique')}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmation de commande</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center' }}>
          <p>Merci ! Votre commande est enregistrée.</p>
          <p>Le récapitulatif vous a été envoyé par e-mail.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            className="btn-org"
            onClick={() => navigate('/boutique')}
          >
            Retour à la boutique
          </Button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { panier, pickupTime } = location.state || { panier: [], pickupTime: '' };
  const { client } = useContext(UserContext);
  const storedPanier = localStorage.getItem('panier');
  const parsedPanier = storedPanier ? JSON.parse(storedPanier) : [];

  const handleSaveCart = async () => {
    if (!client || client.role !== 'pdqve0oMlifjYOu6p52Z') {
      console.error('User is not logged in or user context is undefined.');
      navigate('/login');
      return;
    }

    try {
      const panierId = uuidv4();

      await setDoc(doc(db, 'panier', panierId), {
        id: panierId,
        user: client.id,
        plat: parsedPanier.map((item) => ({
          id: item.id,
          quantite: item.quantity,
        })),
        date_saved: new Date().toLocaleString(),
      });

      alert(`Panier enregistré avec succès avec l'ID : ${panierId}`);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du panier :", error);
    }
  };

  const calculateTotal = () => {
    return parsedPanier
      .reduce((total, plat) => total + parseFloat(plat.prix) * plat.quantity, 0)
      .toFixed(2);
  };

  return (
    <div className="mt-5">
      <div className="content mrg-pg2">
        <div className="row">
          <div className="col">
            <h4 className="tl-pay">Adresse de récupération</h4>
            <div className="card-map mt-5 mb-4 pb-4">
              <iframe
                className="cont-map mb-4"
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d23272.644655200213!2d2.9829036347656084!3d43.186820000000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sfr!2sma!4v1723384275145!5m2!1sfr!2sma"
                loading="lazy"
                title="Adresse du restaurant"
              ></iframe>
              <h6 className="label-check">Adresse de restaurant</h6>
              <p className="text-check">21bis Boulevard Marcel Sembat, 11100 Narbonne</p>
            </div>

            <h4 className="tl-pay mb-5">Informations de paiement</h4>
            <Elements stripe={stripePromise}>
              <CheckoutForm panier={panier} pickupTime={pickupTime} />
            </Elements>
          </div>

          <div className="col-md-1"></div>

          <div className="col-auto">
            <div className="bg-cart rounded">
              <h5 className="tl-cart">Récapitulatif de commande</h5>
              <ul className="list-group mt-4">
                {parsedPanier.map((item) => (
                  <li key={item.id} className="mt-5">
                    <div className="cart-item">
                      <span className="qt-cart mr-2">{item.quantity} x</span>
                      <span className="mr-5">{item.nom}</span>
                      <span className="pr-cart">{item.prix}€</span>
                    </div>
                  </li>
                ))}
                <h4 className="mt-5 total-cart row">
                  <span className="mr-5 col">TOTAL:</span>
                  <span className="col align-self-end">{calculateTotal()}€</span>
                </h4>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;